import React, { useState, useEffect } from 'react';
import Modal from '../../../BaseComponents/Modal';
import TextArea from '../../../BaseComponents/TextArea';
import MultiSelect from '../../../BaseComponents/MultiSelect';
import Button from '../../../BaseComponents/Button';
import { Quote, createQuote, updateQuote } from '../../../../api/quotes';
import { toast } from 'react-toastify';
import './QuotesModal.scss';

interface QuotesModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSaved: (data: any) => void;
    quoteToEdit: Quote | null;
}

const PLAN_OPTIONS = [
    { key: 'core', value: 'Core' },
    { key: 'plus', value: 'Plus' },
    { key: 'scan', value: 'Scan' },
    { key: 'free', value: 'Free' }
];

const QuotesModal: React.FC<QuotesModalProps> = ({
    isOpen,
    onClose,
    onSaved,
    quoteToEdit
}) => {
    const [quote, setQuote] = useState('');
    const [selectedPlans, setSelectedPlans] = useState<{ key: string, value: string }[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (quoteToEdit) {
            setQuote(quoteToEdit.quote);
            const plans = PLAN_OPTIONS.filter(opt => quoteToEdit.plans.includes(opt.key));
            setSelectedPlans(plans);
        } else {
            setQuote('');
            setSelectedPlans([]);
        }
    }, [quoteToEdit]);

    const handlePlanChange = (option: { key: string, value: string }) => {
        setSelectedPlans(prev => {
            if (prev.find(opt => opt.key === option.key)) {
                return prev.filter(opt => opt.key !== option.key);
            }
            return [...prev, option];
        });
    };

    const handleSave = () => {
        if (!quote.trim()) {
            alert('Quote is mandatory');
            return;
        }
        setIsSaving(true);

        const payload = {
            quote: quote.trim(),
            plans: selectedPlans.map(opt => opt.key)
        };

        if (quoteToEdit) {
            updateQuote({ id: quoteToEdit.id, ...payload })
                .then((res) => {
                    toast.success('Quote updated successfully');
                    onSaved({ ...payload, id: res?.id ?? quoteToEdit.id });
                })
                .catch((err) => {
                    toast.error(err.message || 'An error occurred while updating the quote');
                })
                .finally(() => {
                    setIsSaving(false);
                });
        } else {
            createQuote(payload)
                .then((res) => {
                    toast.success('Quote added successfully');
                    onSaved({ ...payload, id: res?.id });
                })
                .catch((err) => {
                    toast.error(err.message || 'An error occurred while saving the quote');
                })
                .finally(() => {
                    setIsSaving(false);
                });
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={quoteToEdit ? 'Edit Quote' : 'Add Quote'}
            width="640px"
        >
            <div className='quotes-modal-content' style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '24px 0' }}>
                <TextArea
                    title='Quote'
                    name='quote'
                    value={quote}
                    placeholder='Enter the quote...'
                    rows={5}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuote(e.target.value)}
                />

                <MultiSelect
                    title='Plans'
                    name='plans'
                    options={PLAN_OPTIONS}
                    selectedOptions={selectedPlans}
                    onChange={(opt: any) => handlePlanChange(opt as { key: string, value: string })}
                    placeholder='Pick one or more plans (Optional)'
                    isError={false}
                />

                <div className='modal-actions' style={{ display: 'flex', flexDirection: 'column', marginTop: '16px' }}>
                    <Button
                        text={isSaving ? 'Saving…' : (quoteToEdit ? 'Update Quote' : 'Add Quote')}
                        onClick={handleSave}
                        isDisabled={!quote.trim() || isSaving}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default QuotesModal;
